import { memoByPalette, SemanticColor, TSemanticColors } from '../../../theme/colors';
import { TButtonOption } from '../../../types/options';

type TCircleHierarchy = TButtonOption['hierarchy'];

const TEXT_COLOR: Record<string, string> = {
    highlighted: 'var(--white_base)',
    primary: 'var(--white_base)',
    secondary: 'var(--purple_100)',
    tertiary: 'var(--black)',
    destructive: 'var(--pink_100)',
};
const TEXT_DISABLED_COLOR: Record<string, string> = {
    highlighted: 'var(--white)',
    primary: 'var(--white)',
    secondary: 'var(--purple_40_lc)',
    tertiary: 'var(--grey_40_lc)',
    destructive: 'var(--pink_40_lc)',
};

const buildBgColor = (colors: TSemanticColors): Record<string, string> => ({
    primary: colors[SemanticColor.FillPrimary],
    secondary: colors[SemanticColor.FillSecondary],
    tertiary: colors[SemanticColor.FillTertiary],
    tertiaryGlass: colors[SemanticColor.FillTertiaryOnDark],
    destructive: colors[SemanticColor.FillTertiary],
    highlighted: 'var(--gradient-blue_1)',
});
const getBgColor = memoByPalette(buildBgColor);

const buildBgDisabledColor = (colors: TSemanticColors): Record<string, string> => ({
    primary: colors[SemanticColor.FillPrimaryDisabled],
    secondary: colors[SemanticColor.FillSecondaryDisabled],
    tertiary: colors[SemanticColor.FillTertiaryDisabled],
    tertiaryGlass: colors[SemanticColor.FillTertiaryOnDarkDisabled],
    destructive: colors[SemanticColor.FillTertiaryDisabled],
    highlighted: 'var(--purple_16_lc)',
});
const getBgDisabledColor = memoByPalette(buildBgDisabledColor);

const buildBgHoverColor = (colors: TSemanticColors): Record<string, string> => ({
    primary: colors[SemanticColor.FillPrimaryHovered],
    secondary: colors[SemanticColor.FillSecondaryHovered],
    tertiary: colors[SemanticColor.FillTertiaryHovered],
    tertiaryGlass: colors[SemanticColor.FillTertiaryOnDarkHovered],
    destructive: colors[SemanticColor.FillCircleDestructiveHovered],
    highlighted: 'var(--purple_72_hc)',
});
const getBgHoverColor = memoByPalette(buildBgHoverColor);

const buildBgPressedColor = (colors: TSemanticColors): Record<string, string> => ({
    primary: colors[SemanticColor.FillCirclePrimaryPressed],
    secondary: colors[SemanticColor.FillSecondaryPressed],
    tertiary: colors[SemanticColor.FillTertiaryPressed],
    tertiaryGlass: colors[SemanticColor.FillTertiaryOnDarkPressed],
    destructive: colors[SemanticColor.FillCircleDestructivePressed],
    highlighted: 'var(--purple_72_tr)',
});
const getBgPressedColor = memoByPalette(buildBgPressedColor);

const buildIconColor = (colors: TSemanticColors): Record<string, string> => ({
    primary: colors[SemanticColor.IconPrimaryInverted],
    emphasised: colors[SemanticColor.IconEmphasised],
    secondary: colors[SemanticColor.IconEmphasised],
    tertiary: colors[SemanticColor.IconPrimary],
    tertiaryGlass: colors[SemanticColor.IconPrimaryOnDarkFill],
    destructive: colors[SemanticColor.IconError],
});
const getIconColor = memoByPalette(buildIconColor);

const buildIconDisabledColor = (colors: TSemanticColors): Record<string, string> => ({
    primary: colors[SemanticColor.IconPrimaryInvertedDisabled],
    emphasised: colors[SemanticColor.IconEmphasisedDisabled],
    secondary: colors[SemanticColor.IconEmphasisedDisabled],
    tertiary: colors[SemanticColor.IconPrimaryDisabled],
    tertiaryGlass: colors[SemanticColor.IconPrimaryOnDarkFillDisabled],
    destructive: colors[SemanticColor.IconErrorDisabled],
});
const getIconDisabledColor = memoByPalette(buildIconDisabledColor);

const key = (h: TCircleHierarchy): string => h ?? 'primary';

export const getTextColor = (h: TCircleHierarchy): string => TEXT_COLOR[key(h)] ?? 'var(--black)';

export const getTextColorDisabled = (h: TCircleHierarchy): string => TEXT_DISABLED_COLOR[key(h)] ?? 'var(--black)';

export const getCircleBg = (h: TCircleHierarchy, colors: TSemanticColors, buttonColor?: string[]): string => {
    if (h === 'highlighted' && Array.isArray(buttonColor) && buttonColor.length) {
        return `linear-gradient(180deg, ${buttonColor.join(', ')})`;
    }
    return getBgColor(colors)[key(h)] ?? 'var(--white)';
};

export const getCircleBgDisabled = (h: TCircleHierarchy, colors: TSemanticColors): string => getBgDisabledColor(colors)[key(h)] ?? 'var(--white)';

export const getCircleHoverBg = (h: TCircleHierarchy, colors: TSemanticColors): string => getBgHoverColor(colors)[key(h)] ?? 'var(--grey_16_lc)';

export const getCirclePressedBg = (h: TCircleHierarchy, colors: TSemanticColors): string => getBgPressedColor(colors)[key(h)] ?? 'var(--grey_8_lc)';

export const getCircleIconColor = (h: TCircleHierarchy, colors: TSemanticColors): string => getIconColor(colors)[key(h)] ?? 'var(--black)';

export const getCircleIconColorDisabled = (h: TCircleHierarchy, colors: TSemanticColors): string => getIconDisabledColor(colors)[key(h)] ?? 'var(--black)';
